import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, X, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { brandName, brandEmail, brandPhone } from '../lib/brand';

const SESSION_KEY = 'stares_chat_session';
const HISTORY_KEY = 'stares_chat_history';

const newSessionId = () => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const SalesAgentChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newSessionId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  });

  const [messages, setMessages] = useState(() => {
    try {
      const stored = sessionStorage.getItem(HISTORY_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return [
      {
        role: 'assistant',
        content: "Hey! I'm Stares — I can help you get a ballpark quote, recommend the right print method, or answer any questions. What are you looking to make? 👕",
      },
    ];
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [unread, setUnread] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist
  useEffect(() => {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
  }, [messages]);

  // Autoscroll on new message / when opening
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Focus input when chat opens
  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  // Tease the user after 8s on the page if they haven't opened chat
  useEffect(() => {
    const t = setTimeout(() => {
      if (!open && messages.length <= 1) setUnread(true);
    }, 8000);
    return () => clearTimeout(t);
  }, [open, messages.length]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await axios.post('/chat/sales-agent', {
        session_id: sessionId,
        message: text,
      });
      const reply = res.data?.reply || "Sorry — give us a call at (786) 814-1010 and we'll help right away.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      if (res.data?.captured_lead) setLeadCaptured(true);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Hmm, I'm having a hiccup. Try calling us directly at ${brandPhone()} or email ${brandEmail()} — we'll get you sorted.`,
      }]);
    } finally {
      setSending(false);
    }
  };

  const quickPrompts = [
    'How much for 50 t-shirts?',
    'I need polos with embroidered logo',
    'What\'s your minimum?',
    'Photo design on 30 shirts',
  ];

  return (
    <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 18 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-[60] bg-[#D90000] hover:bg-[#FF1A1A] text-white rounded-full shadow-2xl shadow-red-900/40 p-4 flex items-center gap-3 group"
            data-testid="chat-bubble"
          >
            <MessageSquare className="w-6 h-6" />
            <span className="hidden sm:inline pr-2 font-bold uppercase tracking-wider text-sm">
              Get a Quote
            </span>
            {unread && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
            )}
            {unread && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full" />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-96 max-w-[420px] h-[600px] max-h-[80vh] bg-[#0A0A0A] border border-white/15 rounded-xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-panel"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1f0000] border-b border-white/10 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D90000] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-bold leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>
                    STARES AI
                  </div>
                  <div className="text-xs text-green-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Online · usually responds instantly
                  </div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1"
                data-testid="chat-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0A0A0A]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#D90000] text-white rounded-br-sm'
                      : 'bg-white/10 text-white/90 rounded-bl-sm'
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white/10 text-white/70 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {leadCaptured && (
                <div className="bg-green-900/30 border border-green-500/30 text-green-300 text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Sent to our team — expect a call within 4 business hours.
                </div>
              )}

              {/* Quick prompts (only on first interaction) */}
              {messages.length <= 1 && !sending && (
                <div className="pt-2 space-y-2" data-testid="chat-quick-prompts">
                  <div className="text-xs text-white/40 uppercase tracking-wider">Try asking:</div>
                  {quickPrompts.map((q, i) => (
                    <button key={i} onClick={() => { setInput(q); setTimeout(() => sendMessage(), 50); }}
                      className="block w-full text-left text-sm px-3 py-2 bg-white/5 hover:bg-white/10 text-white/80 rounded-lg border border-white/10 transition"
                      data-testid={`quick-prompt-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="border-t border-white/10 p-3 bg-[#0F0F0F]" data-testid="chat-form">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Ask about pricing, methods, anything..."
                  rows={1}
                  className="flex-1 bg-[#0A0A0A] border border-white/15 text-white placeholder:text-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#D90000] resize-none max-h-24"
                  data-testid="chat-input"
                />
                <button type="submit" disabled={!input.trim() || sending}
                  className="bg-[#D90000] hover:bg-[#FF1A1A] disabled:opacity-40 disabled:cursor-not-allowed text-white p-2.5 rounded-lg transition"
                  data-testid="chat-send-btn">
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
              <div className="text-[10px] text-white/30 mt-2 text-center">
                AI-powered · Real quotes are confirmed by our team
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SalesAgentChat;
